import { useCallback, useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { fetchAuthSession, signInWithRedirect, signOut as signOutUser } from 'aws-amplify/auth';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { AppShell } from '@/components/layout/app-shell';
import { RepoBrowserPage } from '@/pages/repo-browser-page';
import { RepoRecyclePage } from '@/pages/repo-recycle-page';
import { DevicesPage } from '@/pages/devices-page';
import { NotFoundPage } from '@/pages/not-found';
import { ensureAmplifyConfigured, loadRuntimeConfig, type RuntimeConfig } from '@/lib/runtime-config';
import { getTenantIdFromToken, getUserDisplayNameFromToken } from '@/lib/auth';
import { Loader2, LogIn } from 'lucide-react';

interface AuthState {
  isLoading: boolean;
  isAuthenticated: boolean;
  tenantId: string | null;
  userName: string | null;
  error: string | null;
}

const POST_AUTH_REDIRECT_KEY = 'filesync:postAuthRedirect';
const AUTH_TIMEOUT_MS = 8000;

const isPotentialOAuthCallbackPath = (path: string): boolean => {
  if (!path) {
    return false;
  }
  if (path.startsWith('/auth/callback')) {
    return true;
  }

  const hashSplitIndex = path.indexOf('#');
  const withoutHash = hashSplitIndex >= 0 ? path.slice(0, hashSplitIndex) : path;
  const queryIndex = withoutHash.indexOf('?');
  if (queryIndex < 0) {
    return false;
  }

  const query = withoutHash.slice(queryIndex + 1);
  const params = new URLSearchParams(query);
  const hasAuthResponse = params.has('code') || params.has('error');
  return hasAuthResponse && params.has('state');
};

const decodeUrlSafeHex = (value: string): string | null => {
  if (!value || value.length % 2 !== 0) {
    return null;
  }
  const pairs = value.match(/.{2}/g) ?? [];
  if (pairs.length === 0) {
    return null;
  }
  const chars: string[] = [];
  for (const pair of pairs) {
    const parsed = Number.parseInt(pair, 16);
    if (Number.isNaN(parsed)) {
      return null;
    }
    chars.push(String.fromCharCode(parsed));
  }
  return chars.join('');
};

const getRedirectFromStateParam = (stateParam: string | null): string | null => {
  if (!stateParam) {
    return null;
  }
  const separatorIndex = stateParam.indexOf('-');
  if (separatorIndex <= 0) {
    return null;
  }
  const encodedCustomState = stateParam.slice(separatorIndex + 1);
  const decoded = decodeUrlSafeHex(encodedCustomState);
  if (!decoded || !decoded.startsWith('/')) {
    return null;
  }
  if (isPotentialOAuthCallbackPath(decoded)) {
    return null;
  }
  return decoded;
};

const fetchAuthSessionWithTimeout = async () => Promise.race([
  fetchAuthSession(),
  new Promise<never>((_, reject) => {
    setTimeout(() => reject(new Error('Authentication timed out. Please retry.')), AUTH_TIMEOUT_MS);
  })
]);

const getCurrentLocationPath = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
};

const persistPostAuthRedirect = (path: string) => {
  if (!path) {
    return;
  }
  if (isPotentialOAuthCallbackPath(path)) {
    return;
  }
  try {
    sessionStorage.setItem(POST_AUTH_REDIRECT_KEY, path);
  } catch (error) {
    console.warn('Unable to store post-auth redirect in sessionStorage', error);
  }
  try {
    localStorage.setItem(POST_AUTH_REDIRECT_KEY, path);
  } catch (error) {
    console.warn('Unable to store post-auth redirect in localStorage', error);
  }
};

const consumePostAuthRedirect = (): string | null => {
  const storages: Array<Pick<Storage, 'getItem' | 'removeItem'>> = [];
  if (typeof window !== 'undefined') {
    storages.push(window.sessionStorage, window.localStorage);
  }

  for (const store of storages) {
    try {
      const value = store.getItem(POST_AUTH_REDIRECT_KEY);
      store.removeItem(POST_AUTH_REDIRECT_KEY);
      if (value && value.startsWith('/')) {
        return value;
      }
    } catch (error) {
      console.warn('Unable to read stored post-auth redirect', error);
    }
  }

  return null;
};

const useAuthState = () => {
  const [state, setState] = useState<AuthState>({
    isLoading: true,
    isAuthenticated: false,
    tenantId: null,
    userName: null,
    error: null
  });

  useEffect(() => {
    let cancelled = false;

    const initialise = async () => {
      try {
        await ensureAmplifyConfigured();
        const session = await fetchAuthSessionWithTimeout();
        const token = session.tokens?.idToken?.toString() ?? null;
        const tenantId = getTenantIdFromToken(token);
        const userName = getUserDisplayNameFromToken(token);

        if (!cancelled) {
          setState({
            isLoading: false,
            isAuthenticated: Boolean(session.tokens?.idToken),
            tenantId,
            userName,
            error: tenantId ? null : 'Missing tenant context in your session.'
          });
        }
      } catch (error) {
        if (!cancelled) {
          setState({
            isLoading: false,
            isAuthenticated: false,
            tenantId: null,
            userName: null,
            error: error instanceof Error ? error.message : 'Unable to check authentication state'
          });
        }
      }
    };

    void initialise();

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
};

const AutoSignIn = ({ onSignIn }: { onSignIn: () => Promise<void> }) => {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const start = async () => {
      try {
        await onSignIn();
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unable to start sign in');
        }
      }
    };

    void start();
    return () => {
      cancelled = true;
    };
  }, [onSignIn]);

  return (
    <div className="flex min-h-screen items-center justify-center px-6">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Redirecting to sign in</CardTitle>
          <CardDescription>
            {error ? 'We could not start the sign in flow automatically.' : 'Opening Microsoft Entra ID...'}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-4">
          {error ? (
            <Alert className="border-error/30 bg-error/10">
              <AlertTitle>Sign in failed</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Preparing your secure session
            </div>
          )}
        </CardContent>
        {error ? (
          <CardFooter>
            <Button
              className="w-full"
              onClick={() => {
                setError(null);
                void onSignIn();
              }}
            >
              <LogIn className="h-4 w-4" />
              Sign in with Entra ID
            </Button>
          </CardFooter>
        ) : null}
      </Card>
    </div>
  );
};

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const completeSignIn = async () => {
      try {
        await ensureAmplifyConfigured();
        const session = await fetchAuthSessionWithTimeout();
        if (cancelled) {
          return;
        }
        if (!session.tokens?.idToken) {
          setError('Authentication did not return a valid session. Please try signing in again.');
          return;
        }

        const requestedRedirect = consumePostAuthRedirect();
        const fallbackRedirect = getRedirectFromStateParam(new URLSearchParams(location.search).get('state'));
        const redirectTo = requestedRedirect ?? fallbackRedirect ?? '/';
        navigate(redirectTo, { replace: true });
      } catch {
        if (!cancelled) {
          setError('Unable to complete sign in. Please try again.');
        }
      }
    };

    void completeSignIn();

    return () => {
      cancelled = true;
    };
  }, [location.search, navigate]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6">
        <Card className="w-full max-w-lg">
          <CardHeader>
            <CardTitle className="text-error">Authentication error</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button
              onClick={() => {
                setIsRetrying(true);
                void (async () => {
                  try {
                    await ensureAmplifyConfigured();
                    const currentPath = getCurrentLocationPath();
                    await signInWithRedirect({
                      provider: { custom: 'EntraID' },
                      customState: currentPath && !isPotentialOAuthCallbackPath(currentPath) ? currentPath : undefined
                    });
                  } catch {
                    setIsRetrying(false);
                  }
                })();
              }}
              disabled={isRetrying}
            >
              {isRetrying ? 'Retrying...' : 'Retry sign in'}
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle>Completing sign in</CardTitle>
          <CardDescription>Finalizing your Filesync session.</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Verifying credentials
        </CardContent>
      </Card>
    </div>
  );
};

const AuthenticatedApp = ({
  runtimeConfig,
  tenantId,
  onSignOut,
  userName
}: {
  runtimeConfig: RuntimeConfig;
  tenantId: string;
  onSignOut: () => Promise<void>;
  userName: string | null;
}) => (
  <AppShell onSignOut={onSignOut} userName={userName}>
    <Routes>
      <Route path="/" element={<Navigate to="/repos" replace />} />
      <Route
        path="/repos/*"
        element={<RepoBrowserPage runtimeConfig={runtimeConfig} tenantId={tenantId} />}
      />
      <Route
        path="/repos/:repoId/recycle-bin"
        element={<RepoRecyclePage runtimeConfig={runtimeConfig} tenantId={tenantId} />}
      />
      <Route
        path="/devices"
        element={<DevicesPage runtimeConfig={runtimeConfig} tenantId={tenantId} />}
      />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  </AppShell>
);

function App() {
  const [runtimeConfig, setRuntimeConfig] = useState<RuntimeConfig | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const authState = useAuthState();

  useEffect(() => {
    let cancelled = false;

    const fetchConfig = async () => {
      try {
        const config = await loadRuntimeConfig();
        if (!cancelled) {
          setRuntimeConfig(config);
        }
      } catch (error) {
        if (!cancelled) {
          setConfigError(error instanceof Error ? error.message : 'Unable to load runtime configuration');
        }
      }
    };

    void fetchConfig();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (authState.isAuthenticated || authState.isLoading) {
      return;
    }
    const currentPath = getCurrentLocationPath();
    if (currentPath && !isPotentialOAuthCallbackPath(currentPath)) {
      persistPostAuthRedirect(currentPath);
    }
  }, [authState.isAuthenticated, authState.isLoading]);

  const handleSignIn = useCallback(async () => {
    const currentPath = getCurrentLocationPath();
    if (currentPath && !isPotentialOAuthCallbackPath(currentPath)) {
      persistPostAuthRedirect(currentPath);
    }
    await ensureAmplifyConfigured();
    await signInWithRedirect({
      provider: { custom: 'EntraID' },
      customState: currentPath && !isPotentialOAuthCallbackPath(currentPath) ? currentPath : undefined
    });
  }, []);

  const handleSignOut = useCallback(async () => {
    await ensureAmplifyConfigured();
    await signOutUser();
    window.location.href = '/';
  }, []);

  const renderAuthenticated = useMemo(() => {
    if (!runtimeConfig) {
      return null;
    }

    if (!authState.tenantId) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background px-6">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle className="text-error">Tenant context missing</CardTitle>
              <CardDescription>We could not determine your tenant context.</CardDescription>
            </CardHeader>
            <CardContent>
              <Alert className="border-error/30 bg-error/10">
                <AlertTitle>Next step</AlertTitle>
                <AlertDescription>{authState.error ?? 'Your session is missing tenant details.'}</AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <AuthenticatedApp
        runtimeConfig={runtimeConfig}
        tenantId={authState.tenantId}
        onSignOut={handleSignOut}
        userName={authState.userName}
      />
    );
  }, [authState.error, authState.tenantId, authState.userName, handleSignOut, runtimeConfig]);

  const AppRoutes = () => {
    const location = useLocation();
    const currentPath = `${location.pathname}${location.search}${location.hash}`;
    const shouldHandleOAuthCallback = isPotentialOAuthCallbackPath(currentPath);

    if (configError) {
      return (
        <Routes>
          <Route
            path="*"
            element={
              <div className="flex min-h-screen items-center justify-center bg-background px-6">
                <Card className="w-full max-w-lg">
                  <CardHeader>
                    <CardTitle className="text-error">Configuration error</CardTitle>
                    <CardDescription>We could not load the portal configuration.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Alert className="border-error/30 bg-error/10">
                      <AlertTitle>Details</AlertTitle>
                      <AlertDescription>{configError}</AlertDescription>
                    </Alert>
                  </CardContent>
                </Card>
              </div>
            }
          />
        </Routes>
      );
    }

    if (authState.isLoading || !runtimeConfig) {
      return (
        <Routes>
          <Route
            path="*"
            element={
              <div className="flex min-h-screen items-center justify-center bg-background px-6">
                <Card className="w-full max-w-lg">
                  <CardHeader>
                    <CardTitle>Preparing your session</CardTitle>
                    <CardDescription>Securing the portal and loading repository access.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Authenticating your identity
                    </div>
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-5/6" />
                    <Skeleton className="h-4 w-2/3" />
                  </CardContent>
                </Card>
              </div>
            }
          />
        </Routes>
      );
    }

    return (
      <Routes>
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route
          path="/*"
          element={
            shouldHandleOAuthCallback
              ? <AuthCallback />
              : (authState.isAuthenticated
                ? renderAuthenticated
                : <AutoSignIn onSignIn={handleSignIn} />)
          }
        />
      </Routes>
    );
  };

  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}

export default App;
